import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { useAgentOptions } from '@cotera/client/app/v1/trigger/hooks';
import { useAgentNodeConfig } from '../../../use-agent-node-config';
import { useOpenWorkflowAgent } from '../../../use-open-workflow-resource';
import { AgentToolChips } from '../agent-tool-chips';
import { asRecord, readString } from '../../utils';
import { parseAgentInputShape } from './agent-input-shape';
import type { WorkflowNodeEditorProps } from './types';

/** Config editor for an agent node: pick which agent runs at this step. */
export const AgentNodeEditor: React.FC<WorkflowNodeEditorProps> = ({
  node,
  onChange,
}) => {
  const agents = useAgentOptions();
  // Track the selection locally (seeded from the saved value) so the inputs
  // preview updates the instant a different agent is picked, before saving.
  // The editor is keyed on `node.id` in the drawer, so this remounts (and
  // re-seeds) when switching between nodes.
  const savedAgentId = readString(asRecord(node.data), 'agentId');
  const [agentId, setAgentId] = useState<string | undefined>(savedAgentId);

  // Report the current value so the drawer can save without an explicit edit.
  useEffect(() => {
    const selectedAgent = agents.find((agent) => agent.id === agentId);
    onChange(
      agentId !== undefined
        ? {
            type: 'agent',
            data: {
              agentId,
              ...(selectedAgent !== undefined && {
                name: selectedAgent.title ?? selectedAgent.agentKey,
              }),
            },
          }
        : null
    );
  }, [agentId, agents, onChange]);

  const handleSelect = useCallback((id: string) => {
    setAgentId(id);
  }, []);

  // An agent is a shared resource with its own save state and its own navigation
  // (attached docs push onto the breadcrumb), so it opens as a sibling artifact
  // rather than nested inside this form or drilled into the workflow's stack. As
  // a first-class tab it gets correct Ctrl+S targeting and its own dirty dot for
  // free — no `ActiveArtifactIdOverride` needed — and a trail rooted at itself
  // rather than one claiming the agent is part of this workflow.
  const openWorkflowAgent = useOpenWorkflowAgent();
  const openAgent = useCallback((): void => {
    const selected = agents.find((agent) => agent.id === agentId);
    openWorkflowAgent(agentId, selected?.title ?? selected?.agentKey);
  }, [openWorkflowAgent, agentId, agents]);

  // The agent's tools + input types, resolved live from the selected agent (the
  // hook subscribes to its event stream) so editing the agent's `@input`s/tools
  // shows up here without re-saving the node.
  const { tools, inputShape: liveInputShape } = useAgentNodeConfig(agentId);
  const inputShape = parseAgentInputShape(liveInputShape);

  /**
   * What the selected agent is for, in its own words.
   *
   * The drawer named the agent and its model and stopped there, which is the
   * one thing a title does not tell you: a workflow can hold several agents
   * whose titles are equally terse — `Test`, `Enrich` — and picking between
   * them meant opening each one. Already carried on the option (see
   * `useAgentOptions`), so this costs no extra fetch.
   */
  const description = agents
    .find((agent) => agent.id === agentId)
    ?.description?.trim();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Text.Caption className="text-muted-foreground block">
          Agent
        </Text.Caption>
        <div className="flex items-center gap-2">
          <Select value={agentId ?? ''} onValueChange={handleSelect}>
            <SelectTrigger className="h-9 min-w-0 flex-1">
              <SelectValue placeholder="Select an agent" />
            </SelectTrigger>
            <SelectContent>
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  {agent.title ?? agent.agentKey}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {agentId !== undefined && agentId !== '' ? (
            <Button
              type="button"
              variant="outline"
              title="Open agent"
              aria-label="Open agent"
              className="h-9 shrink-0 gap-2"
              onClick={openAgent}
            >
              <ExternalLink className="h-4 w-4" />
              Open
            </Button>
          ) : null}
        </div>
        {description !== undefined && description !== '' ? (
          // Under the picker rather than beside it: a description is a sentence
          // or several, and the row above is already a select plus a button.
          <Text.Caption className="text-muted-foreground block whitespace-pre-line">
            {description}
          </Text.Caption>
        ) : null}
      </div>

      {inputShape !== null ? (
        <div className="flex flex-col gap-2">
          <Text.Caption className="text-muted-foreground block">
            Inputs
          </Text.Caption>
          {inputShape.kind === 'struct' ? (
            inputShape.fields.length > 0 ? (
              <div className="flex flex-col gap-1.5 rounded-md border p-2">
                {inputShape.fields.map((field) => (
                  <div
                    key={field.name}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="flex min-w-0 items-center gap-1.5">
                      <Text.Caption className="text-foreground truncate font-medium">
                        {field.name}
                      </Text.Caption>
                      {field.required ? (
                        <span className="text-muted-foreground text-[10px] uppercase tracking-wide">
                          required
                        </span>
                      ) : null}
                    </span>
                    <Badge variant="gray" size="sm" className="shrink-0">
                      {field.type}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <Text.Caption className="text-muted-foreground block">
                This agent takes no inputs.
              </Text.Caption>
            )
          ) : (
            <div className="flex items-center justify-between gap-2 rounded-md border p-2">
              <Text.Caption className="text-muted-foreground">
                Accepts
              </Text.Caption>
              <Badge variant="gray" size="sm" className="shrink-0">
                {inputShape.type}
              </Badge>
            </div>
          )}
        </div>
      ) : null}

      {tools.length > 0 ? (
        <div className="flex flex-col gap-2">
          <Text.Caption className="text-muted-foreground block">
            Tools
          </Text.Caption>
          <AgentToolChips tools={tools} />
        </div>
      ) : null}
    </div>
  );
};
